import type { ReactNode } from "react";

/**
 * Tous les chemins de l'application, déclarés une fois : un écran qui renvoie
 * vers un autre écrit `ROUTES.achat`, jamais `"/achat"`. Une chaîne recopiée
 * devient un lien mort le jour où le chemin change.
 *
 * Trois espaces : les écrans publics (connexion, invitation…), l'espace d'une
 * entreprise cliente (les six écrans d'origine plus ses paramètres), et
 * l'espace ESN sous `/esn`.
 */
export const ROUTES = {
  // Publics.
  // La vitrine : la porte d'entrée du site, visible même connecté.
  accueil: "/",
  inscription: "/inscription",
  connexion: "/connexion",
  motDePasseOublie: "/mot-de-passe-oublie",
  // Le jeton voyage dans le fragment (#token=…), jamais dans le chemin.
  reinitialisation: "/reinitialisation",
  invitation: "/invitation",

  // Espace entreprise.
  dashboard: "/tableau-de-bord",
  achat: "/achat",
  catalogue: "/catalogue",
  commande: "/commande",
  missions: "/missions",
  historique: "/historique",
  parametresEquipe: "/parametres/equipe",
  parametresMarque: "/parametres/marque",
  // Cible du retour de la page de signature du prestataire : le back l'écrit
  // en dur (app/billing), les deux doivent rester d'accord.
  parametresPaiement: "/parametres/paiement",
  feuilles: "/feuilles",
  // La cible des courriels, commune à tous les rôles : l'aiguillage renvoie
  // le consultant et l'ESN vers leur espace, l'entreprise la lit ici.
  feuille: "/feuilles/:sheetId",

  // Espace consultant.
  consultant: "/consultant",
  consultantFeuille: "/consultant/feuilles/:sheetId",

  // Espace ESN.
  esnEntreprises: "/esn/entreprises",
  esnEntreprise: "/esn/entreprises/:companyId",
  esnInscriptions: "/esn/inscriptions",
  esnCatalogue: "/esn/catalogue",
  esnEquipe: "/esn/equipe",
  esnMarque: "/esn/marque",
  esnConnexions: "/esn/connexions",
  esnConsultants: "/esn/consultants",
  esnFeuilles: "/esn/feuilles",
  esnFeuille: "/esn/feuilles/:sheetId",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** Les routes qui se visitent sans session. */
export const PUBLIC_ROUTES: readonly string[] = [
  ROUTES.accueil,
  ROUTES.inscription,
  ROUTES.connexion,
  ROUTES.motDePasseOublie,
  ROUTES.reinitialisation,
  ROUTES.invitation,
];

/** Sous-écrans de la fiche d'une entreprise vue par l'ESN. */
export type EsnEntrepriseOnglet = "dashboard" | "commande" | "missions" | "historique" | "achat";

/** Chemin de la fiche d'une entreprise pour l'ESN, ou d'un de ses onglets. */
export function esnEntreprisePath(companyId: string, onglet?: EsnEntrepriseOnglet): string {
  const base = `/esn/entreprises/${companyId}`;
  return onglet === undefined ? base : `${base}/${onglet}`;
}

/** Chemin d'une feuille, selon l'espace où on la lit. */
export function feuillePath(sheetId: string): string {
  return `/feuilles/${sheetId}`;
}

export function consultantFeuillePath(sheetId: string): string {
  return `/consultant/feuilles/${sheetId}`;
}

export function esnFeuillePath(sheetId: string): string {
  return `/esn/feuilles/${sheetId}`;
}

export type NavItem = {
  to: string;
  label: string;
  /** Description courte, affichée en infobulle dans la navigation. */
  hint: string;
  icon: ReactNode;
  /** Correspondance exacte du chemin, pour une entrée dont le chemin préfixe d'autres routes. */
  end?: boolean;
  /** Nombre à afficher en pastille après le libellé ; absent ou nul, rien. */
  count?: number;
  /** Rubrique de la navigation. Un intitulé est rendu à chaque changement de rubrique. */
  group?: string;
};

/* Pictogrammes au trait, dessinés à la même grille de 24 et au même contour :
   un jeu d'icônes emprunté à deux sources se voit immédiatement. */
const traits = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// Fonction et non composant : ce fichier exporte surtout des constantes, et un
// composant exporté au milieu casserait le rechargement à chaud de Vite.
function icone(children: ReactNode) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" {...traits}>
      {children}
    </svg>
  );
}

/*
 * Les libellés disent l'écran, pas le concept. « Achat », « Catalogue » et
 * « Commande » étaient trois noms abstraits : justes vus du modèle, muets pour
 * qui cherche où acheter des tokens ou où lire un tarif. La règle retenue :
 * un verbe quand l'écran fait quelque chose au portefeuille, un nom précis
 * quand il ne fait que montrer.
 *
 * « Nouveau projet » plutôt que « Commander une prestation » : le libellé
 * tient sur une ligne dans 15rem, et il se lit dans la foulée de « Projets »
 * juste en dessous — l'un crée, l'autre suit. Le `hint` porte la nuance que le
 * libellé n'a pas la place de dire.
 *
 * Les rubriques suivent les questions du client plutôt que le modèle : son
 * solde (acheter, relire l'historique), ses projets (en créer, les suivre,
 * lire les tarifs pour les chiffrer), son compte.
 */
export const NAV_ENTREPRISE: NavItem[] = [
  {
    to: ROUTES.dashboard,
    label: "Dashboard",
    hint: "Soldes des quatre seaux",
    icon: icone(
      <>
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="11" width="7" height="10" rx="1.5" />
        <rect x="3" y="15" width="7" height="6" rx="1.5" />
      </>,
    ),
  },
  {
    to: ROUTES.achat,
    label: "Acheter des tokens",
    group: "Portefeuille",
    hint: "Créditer le portefeuille avec un pack",
    icon: icone(
      <>
        <path d="M4 6h2l2.2 9.4a1.5 1.5 0 0 0 1.5 1.1h7.1a1.5 1.5 0 0 0 1.5-1.2L20 8H6.5" />
        <circle cx="10" cy="20" r="1.2" />
        <circle cx="17.5" cy="20" r="1.2" />
      </>,
    ),
  },
  {
    to: ROUTES.historique,
    label: "Historique",
    group: "Portefeuille",
    hint: "Écritures du grand livre",
    icon: icone(
      <>
        <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
        <path d="M3.5 4.5V9H8" />
        <path d="M12 8v4.5l3 1.8" />
      </>,
    ),
  },
  {
    to: ROUTES.commande,
    label: "Nouveau projet",
    group: "Projets",
    hint: "Réserver une enveloppe de tokens",
    icon: icone(
      <>
        <rect x="4" y="5" width="16" height="15" rx="2" />
        <path d="M8 3v4M16 3v4M4 10h16M9.5 14.5l1.8 1.8 3.2-3.6" />
      </>,
    ),
  },
  {
    to: ROUTES.missions,
    label: "Projets",
    group: "Projets",
    hint: "Équipe, consommation, clôture",
    icon: icone(
      <>
        <path d="M9 4.5h6M9 4.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4.5" />
        <path d="M9 4.5H6.5A1.5 1.5 0 0 0 5 6v13a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V6a1.5 1.5 0 0 0-1.5-1.5H15" />
        <path d="M8.5 12.5h7M8.5 16h4" />
      </>,
    ),
  },
  {
    to: ROUTES.catalogue,
    label: "Grille tarifaire",
    group: "Projets",
    hint: "Le tarif journalier de chaque expertise",
    icon: icone(
      <>
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" />
        <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5z" />
      </>,
    ),
  },
];

/*
 * Pictogramme des feuilles de temps : une grille de jours cochés. Partagé par
 * les trois espaces — le même objet doit se reconnaître d'un espace à l'autre.
 */
function iconeFeuilles() {
  return icone(
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M4 10h16" />
      <path d="M8.5 14h2M13.5 14h2M8.5 17.5h2M13.5 17.5h2" />
    </>,
  );
}

/** Entrée « Feuilles de temps » de l'entreprise, pour ses valideurs. */
export const NAV_FEUILLES: NavItem = {
  to: ROUTES.feuilles,
  label: "Feuilles de temps",
  group: "Projets",
  hint: "Valider les jours déclarés par les consultants",
  icon: iconeFeuilles(),
};

/** Navigation de l'espace consultant : une seule entrée, ses feuilles. */
export const NAV_CONSULTANT: NavItem[] = [
  {
    to: ROUTES.consultant,
    label: "Mes feuilles",
    hint: "Déclarer mes jours, projet par projet",
    icon: iconeFeuilles(),
  },
];

/** Entrée « Feuilles de temps » de l'ESN, pour tout compte ESN. */
export const NAV_ESN_FEUILLES: NavItem = {
  to: ROUTES.esnFeuilles,
  label: "Feuilles de temps",
  hint: "Les feuilles de tous les clients",
  icon: iconeFeuilles(),
};

/** Entrée « Consultants » de l'ESN, pour tout compte ESN. */
export const NAV_ESN_CONSULTANTS: NavItem = {
  to: ROUTES.esnConsultants,
  label: "Consultants",
  hint: "Inviter et suivre les intervenants",
  icon: icone(
    <>
      <circle cx="12" cy="8" r="3.2" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </>,
  ),
};

/** Entrée « Paramètres » de l'entreprise, réservée à ses administrateurs. */
export const NAV_PARAMETRES: NavItem = {
  to: ROUTES.parametresEquipe,
  label: "Paramètres",
  group: "Compte",
  hint: "Équipe, marque et paiement de l'entreprise",
  icon: icone(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </>,
  ),
};

/** Navigation de l'espace ESN. */
export const NAV_ESN: NavItem[] = [
  {
    to: ROUTES.esnEntreprises,
    label: "Entreprises",
    hint: "Les clients et leurs portefeuilles",
    icon: icone(
      <>
        <path d="M4 20V6.5A1.5 1.5 0 0 1 5.5 5h7A1.5 1.5 0 0 1 14 6.5V20" />
        <path d="M14 10h4.5A1.5 1.5 0 0 1 20 11.5V20M4 20h16M7.5 9h3M7.5 13h3M7.5 17h3" />
      </>,
    ),
  },
  {
    to: ROUTES.esnInscriptions,
    label: "Inscriptions",
    hint: "Demandes de compte à traiter",
    // Un bac de réception : ce qui arrive de l'extérieur et attend qu'on
    // le prenne.
    icon: icone(
      <>
        <path d="M4 13h4.5l1.5 2.5h4l1.5-2.5H20" />
        <path d="M4 13v4.5A1.5 1.5 0 0 0 5.5 19h13a1.5 1.5 0 0 0 1.5-1.5V13l-2.2-7.1A1.5 1.5 0 0 0 16.4 5H7.6a1.5 1.5 0 0 0-1.4.9z" />
      </>,
    ),
  },
  {
    to: ROUTES.esnCatalogue,
    label: "Grille tarifaire",
    hint: "Le tarif journalier de chaque expertise",
    icon: icone(
      <>
        <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H11v16H5.5A1.5 1.5 0 0 1 4 18.5z" />
        <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H13v16h5.5a1.5 1.5 0 0 0 1.5-1.5z" />
      </>,
    ),
  },
];

/** Entrée « Équipe ESN », réservée aux administrateurs ESN. */
export const NAV_ESN_MARQUE: NavItem = {
  to: ROUTES.esnMarque,
  label: "Marque",
  hint: "Nom, couleur et logo de la plateforme",
  icon: icone(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 0 0 16 3 3 0 0 0 0-6 2 2 0 0 1 0-4h3" />
    </>,
  ),
};

/** Entrée « Connexions », réservée aux administrateurs ESN. */
export const NAV_ESN_CONNEXIONS: NavItem = {
  to: ROUTES.esnConnexions,
  label: "Connexions",
  hint: "Quand chaque compte s'est connecté",
  icon: icone(
    <>
      <path d="M10 4H6.5A1.5 1.5 0 0 0 5 5.5v13A1.5 1.5 0 0 0 6.5 20H10" />
      <path d="M14 8l4 4-4 4M18 12H9" />
    </>,
  ),
};

export const NAV_ESN_EQUIPE: NavItem = {
  to: ROUTES.esnEquipe,
  label: "Équipe ESN",
  hint: "Les comptes de l'ESN",
  icon: icone(
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
      <circle cx="17" cy="9" r="2.4" />
      <path d="M15.5 14.2a4.5 4.5 0 0 1 5 4.3" />
    </>,
  ),
};

